import React, { useMemo } from 'react';
import './timeline-markers.scss';

const getSegmentByZoom = (zoom: number, totalSeconds: number) => {
  // More granular time markers based on zoom level
  const segmentSizes = [10, 5, 2, 1, 0.5]; // In seconds
  let segmentSize = segmentSizes[Math.min(zoom - 1, segmentSizes.length - 1)];

  // For longer videos, use larger segments
  if (totalSeconds > 300) {
    // More than 5 minutes
    segmentSize *= 5;
  } else if (totalSeconds > 120) {
    // More than 2 minutes
    segmentSize *= 2;
  }

  return segmentSize;
};

interface TimelineMarkersProps {
  totalFrames: number;
  fps?: number;
  zoom?: number;
}

const TimelineMarkers: React.FC<TimelineMarkersProps> = ({
  totalFrames,
  fps = 30,
  zoom = 1,
}) => {
  const formatTime = (frame) => {
    const totalSeconds = frame / fps;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const milliseconds = Math.floor((totalSeconds % 1) * 1000);

    // For high zoom levels, show more detail
    if (zoom >= 4 && minutes === 0) {
      return `${seconds}.${milliseconds
        .toString()
        .padStart(3, '0')
        .substring(0, 1)}s`;
    }

    // For videos between 1-90 seconds with minutes
    if (minutes > 0) {
      if (seconds === 0) {
        return `${minutes}m`;
      }
      // Only show seconds if they're significant (not zero)
      return `${minutes}m${seconds}s`;
    }

    // For videos under a minute
    return `${seconds}s`;
  };

  // Calculate the markers based on zoom level
  const { segmentSeconds, numSegments, smallSegments } = useMemo(() => {
    const totalSeconds = totalFrames / fps;
    const segmentSeconds = getSegmentByZoom(zoom, totalSeconds);
    const numSegments = Math.ceil(totalSeconds / segmentSeconds);

    // Calculate small tick marks between main markers
    const smallTicksPerSegment = zoom >= 3 ? 4 : 2;
    const smallSegmentSeconds = segmentSeconds / smallTicksPerSegment;
    const smallSegments: any = [];

    for (let i = 0; i < numSegments; i++) {
      for (let j = 1; j < smallTicksPerSegment; j++) {
        const time = i * segmentSeconds + j * smallSegmentSeconds;
        if (time < totalSeconds) {
          smallSegments.push(time);
        }
      }
    }

    return { segmentSeconds, numSegments, smallSegments };
  }, [totalFrames, fps, zoom]);

  return (
    <div className="time-markers">
      {/* Main time markers */}
      {Array.from({ length: numSegments + 1 }).map((_, i) => {
        const frame = i * segmentSeconds * fps;
        const position = (frame / totalFrames) * 100;

        // Skip markers that would be outside the timeline
        if (position > 100) return null;

        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: `${position}%`,
              top: '4px',
            }}
          >
            {formatTime(frame)}
          </span>
        );
      })}

      {/* Small tick marks */}
      {smallSegments.map((time, i) => {
        const frame = time * fps;
        const position = (frame / totalFrames) * 100;

        return (
          <div
            key={`small-${i}`}
            className="small-tick"
            style={{
              position: 'absolute',
              left: `${position}%`,
              bottom: '0',
              height: '4px',
              width: '1px',
              backgroundColor: '#aaa',
              transform: 'translateX(-50%)',
            }}
          />
        );
      })}
    </div>
  );
};

export default TimelineMarkers;
